import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuItem } from '@/types';
import Price, { formatPrice } from '@/components/ui/Price';
import DishCodeTag from './DishCodeTag';
import { resolveModePrice, type ConsumerMode } from './resolveModePrice';
import { fromPriceCents } from './fromPrice';

interface Props {
  item: MenuItem;
  /** Quantite de ce plat deja dans le panier (toutes lignes confondues). */
  qty: number;
  /** Le plat a des groupes d'options : « + » ouvre la fiche, pas d'increment. */
  hasOptions: boolean;
  /** Le « − » n'a de sens que sur une ligne sans option ni commentaire. */
  canStep: boolean;
  orderingEnabled: boolean;
  color?: string;
  isBuffetDineIn: boolean;
  activeMode: ConsumerMode;
  onOpen: (item: MenuItem, el: HTMLElement) => void;
  onAdd: (item: MenuItem, el: HTMLElement) => void;
  onRemove: (item: MenuItem) => void;
}

/**
 * La ligne de plat du carnet du serveur.
 *
 * La fiche illustree (`DishCard`) est faite pour le client qui decouvre une
 * carte : photo 4/3, description, animation d'entree. Le serveur, lui, connait
 * la carte et cherche une ligne. Ce qu'il lui faut par ecran, c'est le plus de
 * plats possible, un code lisible, et de quoi poser la quantite sans rien
 * ouvrir : une ligne pleine largeur de 48 px au lieu d'une tuile de ~230.
 *
 * Pas de vignette non plus. Elle ne coute pas de hauteur, mais elle prend
 * 48 px sur les ~190 laisses au nom (donc deux lignes au lieu d'une), elle
 * decale le bord gauche des plats qui n'en ont pas, et en donnees reelles elle
 * ment souvent : toute une famille de menus partage la meme image de remplissage
 * et plusieurs plats portent la photo d'un autre. Le code et le nom sont ce que
 * le serveur lit — et ce que porte le bon de cuisine. La photo reste a un
 * geste, dans la fiche.
 *
 * Le « − n + » est toujours rendu, meme a zero : sinon la colonne des prix se
 * decale des qu'une quantite apparait, et toute la liste devient irreguliere
 * en pleine prise de commande.
 */
function WaiterDishRow({
  item,
  qty,
  hasOptions,
  canStep,
  orderingEnabled,
  color,
  isBuffetDineIn,
  activeMode,
  onOpen,
  onAdd,
  onRemove,
}: Props) {
  const { t } = useTranslation();
  // DF-0161 — un groupe obligatoire du plat a ete desactive : plus rien n'est
  // cochable, et `resource_api` refuse desormais le devis. La ligne le dit au
  // lieu de faire un ajout direct muet, qui vendait le plat a plein tarif avec
  // un bon de cuisine ne portant que son nom.
  const unavailable = !!item.unavailableReason;
  const displayPrice = resolveModePrice(item, activeMode);
  const showInclus = isBuffetDineIn && !!item.includedInBuffet;
  // Meme regle que la fiche client : un plat dont le prix vit dans un groupe
  // d'options obligatoire affiche 0 — on montre « a partir de ».
  const fromCents = displayPrice === 0 && !showInclus ? fromPriceCents(item) : null;
  const inCart = qty > 0;

  return (
    <li
      className="flex min-h-[48px] items-stretch border-2 border-ink bg-paper"
      style={{ boxShadow: 'var(--shadow-stamp-sm)' }}
      data-menu-item
      data-waiter-row
    >
      {/* Rappel de couleur de la categorie, en trait : la meme que la pastille
          de la barre et le trait du titre de section. */}
      {color && (
        <span aria-hidden="true" className="w-[6px] shrink-0" style={{ backgroundColor: color }} />
      )}
      <button
        type="button"
        onClick={(e) => onOpen(item, e.currentTarget)}
        disabled={unavailable}
        className={`flex min-w-0 flex-1 items-center gap-2 bg-transparent border-0 px-2 py-1 text-start text-ink${
          unavailable ? ' opacity-50' : ''
        }`}
      >
        <span className="min-w-0 flex-1 font-display text-[14px] leading-[1.2] line-clamp-2">
          {/* `md` et pas `sm` : le code est ce que le serveur compare avec la
              carte papier et ce qu'il annonce en cuisine — a 11 px un « E10 »
              devenait le plus petit texte de la ligne. */}
          <DishCodeTag name={item.name} code={item.code} size="md" />
          {item.name}
        </span>
        <span className="shrink-0 font-mono text-[13px] font-bold">
          {showInclus ? (
            <span className="text-mint-deep">{t('menu.includedInBuffet')}</span>
          ) : fromCents !== null ? (
            t('menu.priceFrom', { price: formatPrice(fromCents) })
          ) : (
            <Price amountCents={displayPrice} saleCents={item.salePrice} className="text-[13px]" />
          )}
        </span>
      </button>

      {orderingEnabled && unavailable && (
        /* Pas de « − n + » : rien n'est ajoutable. Le motif remplace le
           compteur, a la meme place, pour que le serveur lise pourquoi la
           ligne ne repond pas au lieu de retaper dessus. */
        <span
          data-testid={`waiter-unavailable-${item.id}`}
          className="flex shrink-0 items-center border-s-2 border-ink px-2 text-center font-display text-[11px] leading-[1.1] text-ink opacity-70"
        >
          {t('menu.optionGroupInactive')}
        </span>
      )}

      {orderingEnabled && !unavailable && (
        <div className="flex shrink-0 items-stretch border-s-2 border-ink">
          <button
            type="button"
            aria-label={t('menu.quickRemove', { name: item.name })}
            onClick={() => onRemove(item)}
            disabled={!canStep || !inCart}
            className="w-10 bg-paper font-display text-xl leading-none text-ink disabled:opacity-25"
          >
            −
          </button>
          {/* Pastille pleine quand le plat est deja au panier : c'est ce qui
              permet de lire la tournee en cours en parcourant la carte. En
              aplat, pas en teinte : les couleurs du theme sont des `var(--x)`
              nues dans tailwind.config, sans `<alpha-value>` — un `bg-mint/25`
              y sort `rgba(0,0,0,0)`, mesure au navigateur. Pour attenuer le
              zero on passe donc par `opacity`, qui, elle, est fiable. */}
          <span
            data-testid={`waiter-qty-${item.id}`}
            className={`flex w-6 items-center justify-center font-mono text-sm font-bold ${
              inCart ? 'bg-ink text-paper' : 'bg-paper text-ink'
            }`}
            style={inCart ? undefined : { opacity: 0.3 }}
          >
            {qty}
          </span>
          <button
            type="button"
            aria-label={t('menu.quickAdd', { name: item.name })}
            onClick={(e) => (hasOptions ? onOpen(item, e.currentTarget) : onAdd(item, e.currentTarget))}
            className="w-11 bg-hot-pink font-display text-2xl leading-none text-[var(--on-accent)]"
          >
            +
          </button>
        </div>
      )}
    </li>
  );
}

export default memo(WaiterDishRow);
